import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import client from '../api/client';
import { toDateInputValue } from '../utils/dateInput';
import type { User } from '../store/authStore';
import { trackGenerateLead } from '../utils/analytics';
import {
  clearLeadSession,
  readLeadIdFromSession,
  wasLeadConversionFired,
} from '../utils/leadSession';

const DECLARATIONS_EMAIL = 'GetSkimp@GetSkimp.com';
const CURRENT_YEAR = new Date().getFullYear();

export type HomeInsuranceRequestPayload = {
  zipCode: string;
  streetAddress?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  isNewHomePurchase: boolean;
  priorStreetAddress?: string;
  priorAddressLine2?: string;
  priorCity?: string;
  priorState?: string;
  priorZip?: string;
  applicantDateOfBirth?: string;
  hasSpouse: boolean;
  spouseFullName?: string;
  spouseDateOfBirth?: string;
  yearRoofReplaced?: number;
  yearWaterHeaterReplaced?: number;
  waterHeaterLocation?: string;
  waterHeaterCount?: number;
  yearPlumbingUpdated?: number;
  yearAcHeatingReplaced?: number;
  acUnitCount?: number;
  yearElectricalPanelReplaced?: number;
  hasCentralAlarm?: boolean;
  hasTrampoline?: boolean;
  hasPets?: boolean;
  hasSolarPanels?: boolean;
  hasPermanentGenerator?: boolean;
  declarationDocumentBase64?: string;
  declarationDocumentFileName?: string;
  declarationPendingLater?: boolean;
  propertyDetailsPendingLater?: boolean;
  leadId?: number;
};

function YesNoField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="form-group">
      <span id={`${id}-label`} className="home-insurance-label">
        {label} *
      </span>
      <div className="home-insurance-yesno" role="radiogroup" aria-labelledby={`${id}-label`}>
        <label>
          <input
            type="radio"
            name={id}
            checked={value === true}
            onChange={() => onChange(true)}
          />
          Yes
        </label>
        <label>
          <input
            type="radio"
            name={id}
            checked={value === false}
            onChange={() => onChange(false)}
          />
          No
        </label>
      </div>
    </div>
  );
}

function parseYear(value: string): number | null {
  const n = parseInt(value.replace(/\D/g, ''), 10);
  if (Number.isNaN(n) || value.replace(/\D/g, '').length !== 4) return null;
  if (n < 1900 || n > CURRENT_YEAR) return null;
  return n;
}

function HomeInsuranceFormPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [zipCode, setZipCode] = useState('');
  const [profileHasAddress, setProfileHasAddress] = useState(false);
  const [profileHasDob, setProfileHasDob] = useState(false);
  const [profileAddressSummary, setProfileAddressSummary] = useState('');
  const [editingAddress, setEditingAddress] = useState(false);

  const [streetAddress, setStreetAddress] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('TX');

  const [isNewHomePurchase, setIsNewHomePurchase] = useState(false);
  const [priorStreetAddress, setPriorStreetAddress] = useState('');
  const [priorAddressLine2, setPriorAddressLine2] = useState('');
  const [priorCity, setPriorCity] = useState('');
  const [priorState, setPriorState] = useState('');
  const [priorZip, setPriorZip] = useState('');

  const [applicantDateOfBirth, setApplicantDateOfBirth] = useState('');

  const [hasSpouse, setHasSpouse] = useState<boolean | null>(null);
  const [spouseFullName, setSpouseFullName] = useState('');
  const [spouseDateOfBirth, setSpouseDateOfBirth] = useState('');

  const [yearRoofReplaced, setYearRoofReplaced] = useState('');
  const [yearWaterHeaterReplaced, setYearWaterHeaterReplaced] = useState('');
  const [waterHeaterLocation, setWaterHeaterLocation] = useState('');
  const [waterHeaterCount, setWaterHeaterCount] = useState('');
  const [yearPlumbingUpdated, setYearPlumbingUpdated] = useState('');
  const [yearAcHeatingReplaced, setYearAcHeatingReplaced] = useState('');
  const [acUnitCount, setAcUnitCount] = useState('');
  const [yearElectricalPanelReplaced, setYearElectricalPanelReplaced] = useState('');

  const [hasCentralAlarm, setHasCentralAlarm] = useState<boolean | null>(null);
  const [hasTrampoline, setHasTrampoline] = useState<boolean | null>(null);
  const [hasPets, setHasPets] = useState<boolean | null>(null);
  const [hasSolarPanels, setHasSolarPanels] = useState<boolean | null>(null);
  const [hasPermanentGenerator, setHasPermanentGenerator] = useState<boolean | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const type = sessionStorage.getItem('insuranceType');
    const zip = sessionStorage.getItem('zipCode') || '';
    if (type !== 'home' || zip.length !== 5) {
      navigate('/qualify');
      return;
    }
    setZipCode(zip);
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    (async () => {
      try {
        const { data } = await client.get<{ user: User }>('/users/profile');
        const p = data.user;
        const hasAddr = Boolean(p.address?.trim() && p.city?.trim() && p.state?.trim() && p.zipCode?.trim());
        setProfileHasAddress(hasAddr);
        if (hasAddr) {
          setProfileAddressSummary(
            [p.address, p.city, p.state, p.zipCode].filter(Boolean).join(', ')
          );
          setStreetAddress(p.address || '');
          setCity(p.city || '');
          setState(p.state || 'TX');
        } else {
          setStreetAddress(p.address || '');
          setCity(p.city || '');
          setState(p.state || 'TX');
        }
        const dob = toDateInputValue(p.dateOfBirth);
        if (dob) {
          setProfileHasDob(true);
          setApplicantDateOfBirth(dob);
        }
      } catch {
        // Profile prefill is optional
      }
    })();
  }, [isAuthenticated]);

  const buildPayload = (propertyDetailsPendingLater: boolean): HomeInsuranceRequestPayload => {
    const payload: HomeInsuranceRequestPayload = {
      zipCode,
      isNewHomePurchase,
      hasSpouse: hasSpouse === true,
      declarationPendingLater: true,
      propertyDetailsPendingLater,
    };

    const useFormAddress = !profileHasAddress || editingAddress;
    if (useFormAddress) {
      payload.streetAddress = streetAddress.trim();
      payload.addressLine2 = addressLine2.trim();
      payload.city = city.trim();
      payload.state = state.trim();
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(applicantDateOfBirth.trim())) {
      payload.applicantDateOfBirth = applicantDateOfBirth.trim();
    }

    if (isNewHomePurchase) {
      payload.priorStreetAddress = priorStreetAddress.trim();
      payload.priorAddressLine2 = priorAddressLine2.trim();
      payload.priorCity = priorCity.trim();
      payload.priorState = priorState.trim();
      payload.priorZip = priorZip.trim();
    }

    if (hasSpouse === true) {
      payload.spouseFullName = spouseFullName.trim();
      payload.spouseDateOfBirth = spouseDateOfBirth.trim();
    }

    if (!propertyDetailsPendingLater) {
      payload.yearRoofReplaced = parseYear(yearRoofReplaced)!;
      payload.yearWaterHeaterReplaced = parseYear(yearWaterHeaterReplaced)!;
      payload.waterHeaterLocation = waterHeaterLocation.trim();
      payload.waterHeaterCount = parseInt(waterHeaterCount, 10);
      payload.yearPlumbingUpdated = parseYear(yearPlumbingUpdated)!;
      payload.yearAcHeatingReplaced = parseYear(yearAcHeatingReplaced)!;
      payload.acUnitCount = parseInt(acUnitCount, 10);
      payload.yearElectricalPanelReplaced = parseYear(yearElectricalPanelReplaced)!;
      payload.hasCentralAlarm = hasCentralAlarm === true;
      payload.hasTrampoline = hasTrampoline === true;
      payload.hasPets = hasPets === true;
      payload.hasSolarPanels = hasSolarPanels === true;
      payload.hasPermanentGenerator = hasPermanentGenerator === true;
    }

    return payload;
  };

  const validateStep1 = (): string | null => {
    if (!/^\d{5}$/.test(zipCode)) return 'Zip code must be 5 digits.';

    const useFormAddress = !profileHasAddress || editingAddress;
    if (useFormAddress) {
      if (!streetAddress.trim() || !city.trim() || !state.trim()) {
        return 'Complete your home address.';
      }
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(applicantDateOfBirth.trim())) {
      return 'Enter your date of birth (YYYY-MM-DD).';
    }

    if (hasSpouse === null) return 'Tell us if a spouse should be included on this application.';
    if (hasSpouse === true) {
      if (!spouseFullName.trim()) return 'Enter your spouse\'s full name.';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(spouseDateOfBirth.trim())) {
        return 'Enter your spouse\'s date of birth (YYYY-MM-DD).';
      }
    }

    if (isNewHomePurchase) {
      if (!priorStreetAddress.trim() || !priorCity.trim() || !priorState.trim()) {
        return 'Complete your prior address for a new home purchase.';
      }
      if (!/^\d{5}$/.test(priorZip.trim())) return 'Prior zip code must be 5 digits.';
    }

    return null;
  };

  const validateStep2 = (): string | null => {
    const yearFields: [string, string][] = [
      [yearRoofReplaced, 'Year roof was replaced'],
      [yearWaterHeaterReplaced, 'Year water heater was replaced'],
      [yearPlumbingUpdated, 'Year plumbing was updated'],
      [yearAcHeatingReplaced, 'Year AC/heating was replaced'],
      [yearElectricalPanelReplaced, 'Year electrical panel was replaced'],
    ];
    for (const [val, label] of yearFields) {
      if (parseYear(val) === null) return `Enter a valid 4-digit year for ${label}.`;
    }

    if (!waterHeaterLocation.trim()) return 'Enter the location of your water heater.';
    const whCount = parseInt(waterHeaterCount, 10);
    if (Number.isNaN(whCount) || whCount < 0) return 'Enter the number of water heaters.';
    const acCount = parseInt(acUnitCount, 10);
    if (Number.isNaN(acCount) || acCount < 0) return 'Enter the number of AC units.';

    if (hasCentralAlarm === null) return 'Tell us if you have a central monitored alarm.';
    if (hasTrampoline === null) return 'Tell us if you have a trampoline.';
    if (hasPets === null) return 'Tell us if you have any pets.';
    if (hasSolarPanels === null) return 'Tell us if your home has solar panels.';
    if (hasPermanentGenerator === null) return 'Tell us if your home has a permanent generator.';

    return null;
  };

  const submitPayload = async (payload: HomeInsuranceRequestPayload) => {
    const leadId = readLeadIdFromSession();
    const withLead = leadId != null ? { ...payload, leadId } : payload;

    if (!isAuthenticated) {
      sessionStorage.setItem('homeInsuranceFormData', JSON.stringify(withLead));
      sessionStorage.setItem('insuranceType', 'home');
      sessionStorage.setItem('redirectAfterSignup', '/insurance/requests');
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      await client.post('/insurance/home-request', withLead);
      if (!wasLeadConversionFired()) {
        await trackGenerateLead('home');
      }
      sessionStorage.removeItem('homeInsuranceFormData');
      sessionStorage.removeItem('insuranceType');
      sessionStorage.removeItem('zipCode');
      clearLeadSession();
      navigate('/insurance/requests');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; message?: string } } };
      setError(
        ax.response?.data?.error ||
          ax.response?.data?.message ||
          'Failed to submit your home insurance application'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    setError('');
    const msg = validateStep1();
    if (msg) {
      setError(msg);
      return;
    }
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSkipPropertyDetails = async () => {
    setError('');
    const msg = validateStep1();
    if (msg) {
      setError(msg);
      setCurrentStep(1);
      return;
    }
    await submitPayload(buildPayload(true));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const step1Msg = validateStep1();
    if (step1Msg) {
      setError(step1Msg);
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const msg = validateStep2();
    if (msg) {
      setError(msg);
      return;
    }
    await submitPayload(buildPayload(false));
  };

  const sectionHeadingStyle = { color: 'var(--color-green)', margin: '0 0 1rem' } as const;
  const actionButtonsStyle = { display: 'flex', gap: '1rem', marginTop: '1.5rem' } as const;

  return (
    <div className="container home-insurance-form-page" style={{ maxWidth: '720px', margin: '2rem auto' }}>
      <div className="card">
        <div className="card-header">Home insurance application</div>
        <p style={{ textAlign: 'center', color: '#6b7280', margin: '0 0 1rem', fontSize: '0.95rem' }}>
          Step {currentStep} of 2
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {currentStep === 1 && (
            <>
              {profileHasAddress && !editingAddress ? (
                <div
                  className="form-group"
                  style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    background: 'var(--color-light-grey, #f3f4f6)',
                    border: '1px solid var(--color-grey, #e5e7eb)',
                  }}
                >
                  <p style={{ margin: 0, color: '#374151' }}>
                    <strong>Home address:</strong> {profileAddressSummary}
                  </p>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                    <button
                      type="button"
                      onClick={() => setEditingAddress(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'var(--color-green)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      Click here to edit address
                    </button>
                  </p>
                </div>
              ) : (
                <>
                  <h4 style={sectionHeadingStyle}>Home address</h4>
                  <div className="form-group">
                    <label htmlFor="streetAddress">Street address *</label>
                    <input
                      id="streetAddress"
                      value={streetAddress}
                      onChange={(e) => setStreetAddress(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="addressLine2">Apt / suite (optional)</label>
                    <input
                      id="addressLine2"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input id="city" value={city} onChange={(e) => setCity(e.target.value)} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="state">State *</label>
                      <input id="state" value={state} onChange={(e) => setState(e.target.value)} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="zipDisplay">Zip code</label>
                      <input id="zipDisplay" value={zipCode} readOnly />
                    </div>
                  </div>
                </>
              )}

              <h4 style={{ ...sectionHeadingStyle, marginTop: '1.5rem' }}>Household</h4>

              {(!profileHasDob || !/^\d{4}-\d{2}-\d{2}$/.test(applicantDateOfBirth.trim())) && (
                <div className="form-group">
                  <label htmlFor="applicantDateOfBirth">Your date of birth *</label>
                  <input
                    id="applicantDateOfBirth"
                    type="date"
                    value={applicantDateOfBirth}
                    onChange={(e) => setApplicantDateOfBirth(e.target.value)}
                    required
                  />
                </div>
              )}

              <YesNoField
                id="hasSpouse"
                label="Should a spouse be included on this application?"
                value={hasSpouse}
                onChange={setHasSpouse}
              />

              {hasSpouse === true && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                  <div className="form-group">
                    <label htmlFor="spouseFullName">Spouse full name *</label>
                    <input
                      id="spouseFullName"
                      value={spouseFullName}
                      onChange={(e) => setSpouseFullName(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="spouseDateOfBirth">Spouse date of birth *</label>
                    <input
                      id="spouseDateOfBirth"
                      type="date"
                      value={spouseDateOfBirth}
                      onChange={(e) => setSpouseDateOfBirth(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isNewHomePurchase}
                    onChange={(e) => setIsNewHomePurchase(e.target.checked)}
                  />
                  This is a new home purchase
                </label>
              </div>

              {isNewHomePurchase && (
                <>
                  <h4 style={sectionHeadingStyle}>Prior address</h4>
                  <div className="form-group">
                    <label htmlFor="priorStreetAddress">Prior street address *</label>
                    <input
                      id="priorStreetAddress"
                      value={priorStreetAddress}
                      onChange={(e) => setPriorStreetAddress(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="priorAddressLine2">Prior apt / suite (optional)</label>
                    <input
                      id="priorAddressLine2"
                      value={priorAddressLine2}
                      onChange={(e) => setPriorAddressLine2(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label htmlFor="priorCity">Prior city *</label>
                      <input id="priorCity" value={priorCity} onChange={(e) => setPriorCity(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="priorState">Prior state *</label>
                      <input id="priorState" value={priorState} onChange={(e) => setPriorState(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="priorZip">Prior zip code *</label>
                      <input
                        id="priorZip"
                        maxLength={5}
                        value={priorZip}
                        onChange={(e) => setPriorZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      />
                    </div>
                  </div>
                </>
              )}

              <h4 style={{ ...sectionHeadingStyle, marginTop: '1.5rem' }}>Current home insurance</h4>

              <div
                style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  background: 'var(--color-light-grey, #f3f4f6)',
                  border: '1px solid var(--color-grey, #e5e7eb)',
                  marginBottom: '1rem',
                }}
              >
                <p style={{ margin: 0, color: '#374151', fontWeight: 500 }}>
                  I&apos;ll email my declarations page to {DECLARATIONS_EMAIL} later
                </p>
                <p style={{ margin: '0.75rem 0 0', fontSize: '0.9rem', color: '#6b7280' }}>
                  Send your declarations page to <strong>{DECLARATIONS_EMAIL}</strong>. Our team will match it to your
                  application.
                </p>
              </div>

              <div style={actionButtonsStyle}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => navigate('/qualify')}>
                  Back
                </button>
                <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={handleNextStep}>
                  Next
                </button>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <div
                style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  marginBottom: '1.5rem',
                }}
              >
                <p style={{ margin: '0 0 0.75rem', color: '#065f46', fontSize: '0.95rem' }}>
                  Don&apos;t have this information handy? An agent can collect it with you.
                </p>
                <button
                  type="button"
                  className="btn btn-outline"
                  style={{ width: '100%' }}
                  disabled={loading}
                  onClick={handleSkipPropertyDetails}
                >
                  {loading ? 'Submitting…' : 'Fill out information later — an agent will reach out'}
                </button>
              </div>

              <h4 style={sectionHeadingStyle}>Home systems & updates</h4>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                <div className="form-group">
                  <label htmlFor="yearRoofReplaced">Year roof was replaced *</label>
                  <input
                    id="yearRoofReplaced"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="e.g. 2018"
                    value={yearRoofReplaced}
                    onChange={(e) => setYearRoofReplaced(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="yearWaterHeaterReplaced">Year water heater was replaced *</label>
                  <input
                    id="yearWaterHeaterReplaced"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="e.g. 2020"
                    value={yearWaterHeaterReplaced}
                    onChange={(e) => setYearWaterHeaterReplaced(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="waterHeaterLocation">Location of water heater *</label>
                  <input
                    id="waterHeaterLocation"
                    value={waterHeaterLocation}
                    onChange={(e) => setWaterHeaterLocation(e.target.value)}
                    placeholder="e.g. Garage, attic, closet"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="waterHeaterCount">Number of water heaters *</label>
                  <input
                    id="waterHeaterCount"
                    inputMode="numeric"
                    value={waterHeaterCount}
                    onChange={(e) => setWaterHeaterCount(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="yearPlumbingUpdated">Year plumbing was updated *</label>
                  <input
                    id="yearPlumbingUpdated"
                    inputMode="numeric"
                    maxLength={4}
                    value={yearPlumbingUpdated}
                    onChange={(e) => setYearPlumbingUpdated(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="yearAcHeatingReplaced">Year AC/heating was replaced *</label>
                  <input
                    id="yearAcHeatingReplaced"
                    inputMode="numeric"
                    maxLength={4}
                    value={yearAcHeatingReplaced}
                    onChange={(e) => setYearAcHeatingReplaced(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="acUnitCount">Number of AC units *</label>
                  <input
                    id="acUnitCount"
                    inputMode="numeric"
                    value={acUnitCount}
                    onChange={(e) => setAcUnitCount(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="yearElectricalPanelReplaced">Year electrical panel was replaced *</label>
                  <input
                    id="yearElectricalPanelReplaced"
                    inputMode="numeric"
                    maxLength={4}
                    value={yearElectricalPanelReplaced}
                    onChange={(e) => setYearElectricalPanelReplaced(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                </div>
              </div>

              <h4 style={{ ...sectionHeadingStyle, marginTop: '1.5rem' }}>Property features</h4>

              <YesNoField
                id="centralAlarm"
                label="Do you have a central monitored alarm for fire & theft?"
                value={hasCentralAlarm}
                onChange={setHasCentralAlarm}
              />
              <YesNoField
                id="trampoline"
                label="Do you have a trampoline?"
                value={hasTrampoline}
                onChange={setHasTrampoline}
              />
              <YesNoField
                id="pets"
                label="Do you have any pets?"
                value={hasPets}
                onChange={setHasPets}
              />
              <YesNoField
                id="solarPanels"
                label="Is your home equipped with solar panels?"
                value={hasSolarPanels}
                onChange={setHasSolarPanels}
              />
              <YesNoField
                id="permanentGenerator"
                label="Is your home equipped with a permanent generator?"
                value={hasPermanentGenerator}
                onChange={setHasPermanentGenerator}
              />

              <div style={actionButtonsStyle}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  disabled={loading}
                  onClick={() => {
                    setError('');
                    setCurrentStep(1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Back
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
                  {loading ? 'Submitting…' : 'Submit application'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>

      <style>{`
        .home-insurance-yesno {
          display: flex;
          gap: 1.25rem;
          margin-top: 0.35rem;
        }
        .home-insurance-yesno label {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-weight: 500;
          cursor: pointer;
        }
        .home-insurance-label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
      `}</style>
    </div>
  );
}

export default HomeInsuranceFormPage;
